import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {Button} from '@/components/ui/button'
import {GroupHeader} from '@/components/help-icon'
import {AccionLigadaLink} from '@/components/accion-ligada-link'
import {CrearMejoraDialog} from '@/components/crear-mejora-dialog'
import {JERARQUIAS, useCrearMejora, type Fila, type SgsstActionJerarquia} from '@/hooks/sgsst/riesgos/use-ipevr'
import {useBusiness} from '@/hooks/empresa/organizacion/use-businesses'

/** Levels in the order Decreto 1072 art. 2.2.4.6.24 mandates: eliminación first, EPP last. */
export function MedidasSection({businessId, fila, canEdit}: {businessId: string; fila: Fila; canEdit: boolean}) {
  const {t} = useTranslation()
  // `titulo` null = una medida escrita por el dueño, no una del starter-pack (KAI-153).
  const [creating, setCreating] = useState<{jerarquia: SgsstActionJerarquia; titulo: string | null} | null>(null)
  const crearMejora = useCrearMejora(businessId)
  const {data: business} = useBusiness(businessId)

  const byJerarquia = new Map<SgsstActionJerarquia, string[]>()
  for (const m of fila.medidas_sugeridas) {
    if (!byJerarquia.has(m.jerarquia)) byJerarquia.set(m.jerarquia, [])
    byJerarquia.get(m.jerarquia)!.push(m.texto)
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <GroupHeader
          label={t('ipevr.groups.medidas')}
          help={t('ipevr.groups.medidasHelp')}
          testId="fila-details-help-medidas"
        />
        {fila.accion_count > 0 && (
          <Link
            to={`/businesses/${businessId}/sgsst/plan`}
            data-testid="medidas-ver-plan"
            className="inline-flex items-center rounded-sm bg-secondary px-1.5 py-0.5 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase break-words hover:bg-accent hover:text-accent-foreground">
            {t('ipevr.grid.plan', {count: fila.accion_count})}
          </Link>
        )}
      </div>

      {fila.acciones.length > 0 && (
        <div className="rounded-lg border border-border p-3" data-testid="medidas-acciones-ligadas">
          <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('mejora.ligadas')}
          </span>
          <ul className="mt-1.5 space-y-1">
            {fila.acciones.map((accion) => (
              <li key={accion.id}>
                <AccionLigadaLink businessId={businessId} accion={accion} testId="fila-accion-ligada" />
              </li>
            ))}
          </ul>
        </div>
      )}

      <ol className="space-y-2" data-testid="medidas-jerarquia">
        {JERARQUIAS.map((jerarquia, i) => {
          const sugerencias = byJerarquia.get(jerarquia) ?? []
          const hasSugerencias = sugerencias.length > 0
          return (
            <li
              key={jerarquia}
              data-testid={`medidas-nivel-${jerarquia}`}
              className={
                hasSugerencias
                  ? 'space-y-2 rounded-lg border border-border p-3'
                  : 'flex flex-wrap items-center gap-x-2 gap-y-1'
              }>
              <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="font-mono text-[10px] font-medium text-muted-foreground tabular-nums">{i + 1}.</span>
                  <span className="font-mono text-[10px] font-medium tracking-wider text-foreground uppercase">
                    {t(`ipevr.jerarquia.${jerarquia}`)}
                  </span>
                </span>
                {canEdit && (
                  <button
                    type="button"
                    data-testid={`medidas-agregar-${jerarquia}`}
                    onClick={() => setCreating({jerarquia, titulo: null})}
                    className="font-mono text-[11px] text-primary hover:underline">
                    {t('ipevr.medidas.agregar')}
                  </button>
                )}
              </span>
              {hasSugerencias ? (
                <ul className="space-y-1.5">
                  {sugerencias.map((texto) => (
                    <li
                      key={texto}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-secondary/50 px-2.5 py-1.5"
                      data-testid="medidas-sugerida-item">
                      <span className="min-w-0 text-sm break-words text-foreground">{texto}</span>
                      {canEdit && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          data-testid="medidas-sugerida-crear"
                          onClick={() => setCreating({jerarquia, titulo: texto})}>
                          {t('ipevr.medidas.crearEstaAccion')}
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-xs text-muted-foreground">{t('ipevr.medidas.sinSugerenciasNivel')}</span>
              )}
            </li>
          )
        })}
      </ol>

      {creating && (
        <CrearMejoraDialog
          businessId={businessId}
          open
          onOpenChange={(open) => !open && setCreating(null)}
          origen={{
            etiqueta: t('mejora.origen.medida', {nivel: t(`ipevr.jerarquia.${creating.jerarquia}`)}),
            titulo: fila.descripcion,
            detalle: t(`ipevr.clasificacion.${fila.clasificacion}`),
            testId: 'crear-medida-dialog',
          }}
          descripcion={creating.titulo ?? ''}
          descripcionPlaceholder={creating.titulo === null ? t('ipevr.medidas.agregarPlaceholder') : undefined}
          responsableId={business?.legal_representative_id ?? null}
          onCrear={(editado) =>
            crearMejora.mutateAsync({
              filaId: fila.id,
              jerarquia: creating.jerarquia,
              titulo: creating.titulo ?? undefined,
              editado,
            })
          }
        />
      )}
    </section>
  )
}
